import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { BrowserView, MobileView } from 'react-device-detect';
import { getSharedRecipe } from '../../api/nomnom/public';
import DisplayBrowser from './DisplayBrowser';
import DisplayMobile from './DisplayMobile';
import LoadingState from './components/LoadingState';
import ErrorState from './components/ErrorState';
import { NOMNOM_URL } from '../../config';

const PublicRecipe = () => {
  const { token } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        setLoading(true);
        const response = await getSharedRecipe(token);
        setRecipe(response);
        setError(null);
      } catch (err) {
        console.error('Error fetching shared recipe:', err);
        setError(err.response?.status === 404 ? 'not-found' : 'error');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchRecipe();
    }
  }, [token]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState type={error} />;
  if (!recipe) return <ErrorState type="error" />;

  // Build absolute image URL if needed
  const buildImageUrl = (imageUrl) => {
    if (!imageUrl) return '';

    // If already absolute URL, return as-is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }

    // If starts with /nomnom/api/, use base domain only
    if (imageUrl.startsWith('/nomnom/api/')) {
      const baseUrl = NOMNOM_URL.replace(/\/nomnom\/api\/?$/, '');
      return `${baseUrl}${imageUrl}`;
    }

    // Otherwise append to NOMNOM_URL
    return `${NOMNOM_URL}${imageUrl}`;
  };

  const imageUrl = buildImageUrl(recipe.coverImage?.imageUrl);

  // Update recipe with absolute image URL for display components
  const recipeWithAbsoluteImage = {
    ...recipe,
    coverImage: recipe.coverImage
      ? {
        ...recipe.coverImage,
        imageUrl: imageUrl
      }
      : null
  };

  return (
    <>
      <Helmet>
        <title>{recipe.name} - Shared Recipe | NomNom</title>
        <meta name="description" content={recipe.description || `View ${recipe.name} shared from NomNom`} />
        <meta property="og:title" content={`${recipe.name} - Shared Recipe`} />
        <meta property="og:description" content={recipe.description || `View ${recipe.name} shared from NomNom`} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${recipe.name} - Shared Recipe`} />
        <meta name="twitter:description" content={recipe.description || `View ${recipe.name} shared from NomNom`} />
        <meta name="twitter:image" content={imageUrl} />

        {/* Schema.org JSON-LD for recipe rich results */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Recipe',
            name: recipe.name,
            description: recipe.description || '',
            recipeIngredient: recipe.ingredients?.map(
              ing => `${ing.amountString || ing.amount} ${ing.unitOfMeasureAbbreviation || ing.unitOfMeasure} ${ing.name}`
            ) || [],
            recipeInstructions: recipe.directions || '',
            recipeYield: recipe.servings ? `${recipe.servings} servings` : '',
            image: imageUrl
          })}
        </script>
      </Helmet>

      <BrowserView>
        <DisplayBrowser recipe={recipeWithAbsoluteImage} />
      </BrowserView>
      <MobileView>
        <DisplayMobile recipe={recipeWithAbsoluteImage} />
      </MobileView>
    </>
  );
};

export default PublicRecipe;
